// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export interface Point {
    coordinates: string[];
    productNumber: string;
    sortOrder: number;
    description: string;
    qty: number;
    displaySequence: boolean;
}

export interface Kit {
    kitProducts: Point[];
    productNumber: string;
}

export interface Bundle {
    bundleProducts: { description: string; productNumber: string }[];
    productNumber: string;
}
export interface BomImageModel {
    kits: Kit[];
    productNumber: string;
    products: Point[];
    bundles: Bundle[];
}

export interface BomModel {
    billOfMaterialImages: BomImageModel[];
    productIdLookup: { [key: string]: string };
}

export interface xProductBomState {
    [key: string]: {
        isLoading: boolean;
        data: BomModel;
        failedMessage: string;
        products: { [erp: string]: ProductModel };
        productsInfo: { [erp: string]: any };
        sortOrders: { [productNumber: string]: number };
        bundles: { [productNumber: string]: Bundle };
        productsInitialQuantities: { [id: string]: number };
        productsTempQuantities: { [id: string]: number };
    };
}

const initialState: xProductBomState = {};

const reducer = {
    "xProductBom/BeginLoadProductBom": (draft: Draft<xProductBomState>, action: { key: string }) => {
        draft[action.key] = {
            isLoading: true,
            data: { billOfMaterialImages: [], productIdLookup: {} },
            failedMessage: "",
            products: {},
            productsInfo: {},
            sortOrders: {},
            bundles: {},
            productsInitialQuantities: {},
            productsTempQuantities: {},
        };
    },
    "xProductBom/FailedToLoadProductBom": (
        draft: Draft<xProductBomState>,
        action: { failedMessage: string; key: string },
    ) => {
        draft[action.key] = {
            isLoading: false,
            data: { billOfMaterialImages: [], productIdLookup: {} },
            failedMessage: action.failedMessage,
            products: {},
            productsInfo: {},
            sortOrders: {},
            bundles: {},
            productsInitialQuantities: {},
            productsTempQuantities: {},
        };
    },
    "xProductBom/CompleteLoadProductBom": (draft: Draft<xProductBomState>, action: { data: BomModel; key: string }) => {
        const sortOrders: { [productNumber: string]: number } = {};
        const productsInitialQuantities: { [id: string]: number } = {};

        action.data?.billOfMaterialImages?.[0]?.products?.forEach(product => {
            sortOrders[product.productNumber] = product.sortOrder;
            const id = action.data?.productIdLookup?.[product.productNumber];
            productsInitialQuantities[id] = product.qty;
        });
        const productsTempQuantities = productsInitialQuantities;

        // special case - a product might exists in a kit but not in all products list
        action.data?.billOfMaterialImages?.[0]?.kits?.forEach(kit => {
            kit?.kitProducts?.forEach(product => {
                sortOrders[product.productNumber] = product.sortOrder;
            });
        });

        const bundles: { [productNumber: string]: Bundle } = {};
        action.data?.billOfMaterialImages?.[0]?.bundles?.forEach(bundle => {
            bundles[bundle.productNumber] = bundle;
        });

        draft[action.key] = {
            isLoading: false,
            data: action.data,
            failedMessage: "",
            products: {},
            productsInfo: {},
            sortOrders,
            bundles,
            productsInitialQuantities,
            productsTempQuantities,
        };
    },

    "xProductBom/CompleteLoadProducts": (
        draft: Draft<xProductBomState>,
        action: { products: { [erp: string]: ProductModel }; key: string },
    ) => {
        draft[action.key] = {
            isLoading: false,
            data: draft[action.key].data,
            failedMessage: "",
            products: action.products,
            productsInfo: {},
            sortOrders: draft[action.key].sortOrders,
            bundles: draft[action.key].bundles,
            productsInitialQuantities: draft[action.key].productsInitialQuantities,
            productsTempQuantities: draft[action.key].productsTempQuantities,
        };
    },
    "xProductBom/CompleteLoadProductsInfo": (
        draft: Draft<xProductBomState>,
        action: { productsInfo: any; key: string },
    ) => {
        draft[action.key] = {
            isLoading: false,
            data: draft[action.key].data,
            failedMessage: "",
            products: draft[action.key].products,
            productsInfo: action.productsInfo,
            sortOrders: draft[action.key].sortOrders,
            bundles: draft[action.key].bundles,
            productsInitialQuantities: draft[action.key].productsInitialQuantities,
            productsTempQuantities: draft[action.key].productsTempQuantities,
        };
    },
    "xProductBom/UpdateQuantity": (
        draft: Draft<xProductBomState>,
        action: { productId: string; quantity: number; key: string },
    ) => {
        const tempQuantities = draft[action.key].productsTempQuantities;
        tempQuantities[action.productId] = action.quantity;

        draft[action.key] = {
            isLoading: false,
            data: draft[action.key].data,
            failedMessage: "",
            products: draft[action.key].products,
            productsInfo: draft[action.key].productsInfo,
            sortOrders: draft[action.key].sortOrders,
            bundles: draft[action.key].bundles,
            productsInitialQuantities: draft[action.key].productsInitialQuantities,
            productsTempQuantities: { ...tempQuantities },
        };
    },
    "xProductBom/ResetQuantities": (draft: Draft<xProductBomState>, action: { key: string }) => {
        draft[action.key] = {
            isLoading: false,
            data: draft[action.key].data,
            failedMessage: "",
            products: draft[action.key].products,
            productsInfo: draft[action.key].productsInfo,
            sortOrders: draft[action.key].sortOrders,
            bundles: draft[action.key].bundles,
            productsInitialQuantities: draft[action.key].productsInitialQuantities,
            productsTempQuantities: draft[action.key].productsInitialQuantities,
        };
    },
};

const xProductBom = createTypedReducerWithImmer(initialState, reducer);
(reducers as any).xProductBom = xProductBom;

export type xProductBom = {
    xProductBom: xProductBomState;
} & ApplicationState;

type xProductBomReducers = {
    [key: string]: typeof xProductBom;
};

export type xFeaturedCategoriesActions = Parameters<xProductBomReducers[keyof xProductBomReducers]>[1];
